import {
  ButtonHTMLAttributes,
  MouseEventHandler,
  ReactElement,
  ReactNode,
  useEffect,
  useState,
} from 'react';
import { useSelect } from 'downshift';
import { CSSObject, SerializedStyles } from '@emotion/react';

import { Box } from '@reckon-web/box';
import { PopoverDialog, usePopper } from '@reckon-web/popover';
import { useTheme } from '@reckon-web/theme';

import { Menu, MenuItem, SelectMenuButton } from './styled-components';

// Prep
// ------------------------------

export const MENU_ALIGNMENT = {
  left: 'bottom-start',
  right: 'bottom-end',
} as const;

// Types
// ------------------------------

type UnionToIntersection<T> = (T extends any ? (x: T) => any : never) extends (
  x: infer R
) => any
  ? R
  : never;

type ItemToLabel<Option> = UnionToIntersection<
  Option extends { label: string }
    ? { itemToLabel?: (option: Option) => string }
    : { itemToLabel: (option: Option) => string }
>;

type ItemToValue<Option> = UnionToIntersection<
  Option extends { value: string | number }
    ? { itemToValue?: (option: Option) => string | number }
    : { itemToValue: (option: Option) => string | number }
>;

export type AnchorElementType =
  | HTMLAnchorElement
  | HTMLButtonElement
  | HTMLDivElement
  | HTMLSpanElement;

type AlignType = keyof typeof MENU_ALIGNMENT;

type TriggerRendererType = (options: {
  isOpen: boolean;
  hasValue: boolean;
  triggerProps: ButtonHTMLAttributes<HTMLButtonElement>;
}) => ReactElement;

export type SelectMenuProps<Option> = {
  /** Direction to align the menu dialog relative to the trigger element. */
  align?: AlignType;
  id?: string;
  /** Control each item is rendered within the menu. */
  itemRenderer?: (option: Option) => ReactNode;
  /** Determine if a given option should be disabled within the menu. */
  itemToDisabled?: (option: Option) => boolean;
  /** Map each option to its appropriate label. */
  itemToLabel?: (option: Option) => string;
  /** Map each option to its appropriate value. */
  itemToValue?: (option: Option) => string | number;
  /** Called when an option is selected. */
  onChange: (value: Option) => void;
  /** Support a "clear" action in the **default** trigger button. */
  onClear?: MouseEventHandler;
  /** Array of options for the user to select from. */
  options: Option[];
  /** The element to be rendered as the dialog trigger. */
  trigger: ReactNode | TriggerRendererType;
  /** The selected value. Must match the return value from `itemToValue`. */
  value: Option | Array<Option> | undefined;
  css?: CSSObject | SerializedStyles;
} & ItemToLabel<Option> &
  ItemToValue<Option>;

// Main export
// ------------------------------

export function SelectMenu<Option>({
  align = 'left',
  itemRenderer: consumerRenderer,
  // @ts-ignore
  itemToDisabled = (item) => Boolean(item.disabled),
  // @ts-ignore
  itemToLabel = (item) => item.label,
  // @ts-ignore
  itemToValue = (item) => item.value,
  onChange,
  onClear,
  options,
  trigger,
  value,
  // @ts-ignore
  ...props
}: SelectMenuProps<Option>) {
  // create the default renderer (can't happen in destructuring because we need `itemToLabel`)
  const itemRenderer =
    consumerRenderer || ((item: Option) => itemToLabel(item));

  // handle downshift type requirement here rather than passing on to consumers
  const itemToString = (item: Option | null) => (item ? itemToLabel(item) : '');

  // setup downshift (selection/navigation handling)
  const { isOpen, getToggleButtonProps, getMenuProps, getItemProps } =
    useSelect({
      items: options,
      itemToString,
      isItemDisabled: itemToDisabled, // Move disabled logic here
      selectedItem: Array.isArray(value) ? null : value, // stop downshift from disallowing selection of an existing item when multi-select
      stateReducer: (state, actionAndChanges) => {
        const { changes, type } = actionAndChanges;
        if (Array.isArray(value)) {
          switch (type) {
            case useSelect.stateChangeTypes.ToggleButtonKeyDownEnter:
            case useSelect.stateChangeTypes.ToggleButtonKeyDownSpaceButton:
            case useSelect.stateChangeTypes.ItemClick:
              return {
                ...changes,
                isOpen: true, // keep the menu open after selection
                highlightedIndex: state.highlightedIndex, // maintain the currently highlighted item
              };
          }
        }

        return changes;
      },
      onSelectedItemChange: ({ selectedItem }) => {
        if (selectedItem) {
          onChange(selectedItem);
        }
      },
    });

  // setup popper (position handling)
  const { setAnchorElement, setPopoverElement, styles, attributes } =
    useSelectPopper({ align, isOpen });

  const triggerProps = getToggleButtonProps({
    ref: (element: AnchorElementType | null) => setAnchorElement(element),
  });

  // setup trigger (the button that invokes the menu)
  const hasValue = Boolean(
    Array.isArray(value) ? value?.length : value !== undefined
  );
  const triggerElement =
    typeof trigger === 'function' ? (
      trigger({ isOpen, hasValue, triggerProps })
    ) : (
      <SelectMenuButton hasValue={hasValue} onClear={onClear} {...triggerProps}>
        {trigger}
      </SelectMenuButton>
    );

  return (
    <Box {...props}>
      {triggerElement}
      <PopoverDialog
        ref={setPopoverElement}
        isVisible={isOpen}
        style={{ ...styles.popper, overflow: 'hidden' }} // crop highlighted options so they don't spill over the rounded corners
        {...attributes.popper}
      >
        <Menu {...getMenuProps({ role: 'menu' }, { suppressRefError: true })}>
          {
            // We need a reference to the menu element to prepare keyboard navigation, but
            // we don't want the menu items to be focusable until the menu is open
            !isOpen
              ? null
              : options.map((item, index) => {
                  const itemValue = itemToValue(item);
                  const isSelected = Array.isArray(value)
                    ? value.map(itemToValue).includes(itemValue)
                    : value && itemToValue(value) === itemValue;

                  const { onClick, ...otherProps } = getItemProps({
                    // Remove the disabled prop from here - it's now handled by isItemDisabled in useSelect
                    item,
                    index,
                    role: Array.isArray(value)
                      ? 'menuitemcheckbox'
                      : 'menuitemradio',
                  });

                  return (
                    <MenuItem
                      key={itemValue}
                      isSelected={isSelected}
                      {...otherProps}
                      {...(onClick && { onClick: onClick as any })}
                    >
                      {itemRenderer(item)}
                    </MenuItem>
                  );
                })
          }
        </Menu>
      </PopoverDialog>
    </Box>
  );
}

// Utils
// ------------------------------

export function useSelectPopper({
  isOpen,
  align,
}: {
  isOpen: boolean;
  align: AlignType;
}) {
  const { spacing } = useTheme();

  // setup popper (position handling)
  const [anchorElement, setAnchorElement] = useState<AnchorElementType | null>(
    null
  );
  const [popoverElement, setPopoverElement] = useState<HTMLDivElement | null>();

  const { styles, attributes, update } = usePopper(
    anchorElement,
    popoverElement,
    {
      placement: MENU_ALIGNMENT[align],
      modifiers: [
        {
          name: 'offset',
          options: {
            offset: [0, spacing.small],
          },
        },
      ],
    }
  );

  // update popper when it opens to get the latest placement
  // useful for prerendered popovers in modals etc.
  useEffect(() => {
    if (update && isOpen) {
      update();
    }
  }, [isOpen, update]);

  return {
    attributes,
    styles,
    setAnchorElement,
    setPopoverElement,
  };
}
